import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { api } from '../features/auth/authStore';
import toast from 'react-hot-toast';
import { timerPersistence } from '../utils/timerPersistence';

export interface ActiveTimer {
  id: string;
  taskId?: string;
  taskTitle?: string;
  description: string;
  startTime: Date;
  duration: number;
  billable: boolean;
  status: string;
}

export interface TimeEntry {
  id: string;
  taskId?: string;
  description: string;
  startTime: Date;
  endTime?: Date;
  duration: number;
  billable: boolean;
  status: string;
}

interface TimerContextType {
  activeTimer: ActiveTimer | null;
  elapsedTime: number;
  loading: boolean;
  hasRecoveredTimer: boolean;
  startTimer: (taskId?: string, description?: string) => Promise<void>;
  stopTimer: () => Promise<void>;
  loadActiveTimer: () => Promise<void>;
  createTimeEntry: (entry: Partial<TimeEntry>) => Promise<void>;
  getTimeEntries: (filters?: any) => Promise<TimeEntry[]>;
  getTimeStats: (startDate?: Date, endDate?: Date) => Promise<any>;
}

const TimerContext = createContext<TimerContextType | undefined>(undefined);

interface TimerProviderProps {
  children: ReactNode;
  userId?: string;
}

export const TimerProvider: React.FC<TimerProviderProps> = ({ children, userId }) => {
  const [activeTimer, setActiveTimer] = useState<ActiveTimer | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasRecoveredTimer, setHasRecoveredTimer] = useState(false);

  // Timer tick effect with persistence
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (activeTimer && userId) {
      interval = setInterval(() => {
        const elapsed = timerPersistence.getElapsedTime(activeTimer);
        setElapsedTime(elapsed);

        // Save to localStorage periodically
        timerPersistence.saveTimer(activeTimer, userId);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTimer, userId]);

  // Load active timer when userId changes
  useEffect(() => {
    if (userId) {
      loadActiveTimer();
    }
  }, [userId]);

  const loadActiveTimer = useCallback(async () => {
    if (!userId) return;

    try {
      const { serverTimer, localTimer, needsRecovery } = await timerPersistence.syncWithServer(userId);

      if (needsRecovery && localTimer) {
        // Try to recover the local timer
        const recoveredTimer = await timerPersistence.recoverTimer(localTimer, userId);
        if (recoveredTimer) {
          setActiveTimer(recoveredTimer);
          setElapsedTime(timerPersistence.getElapsedTime(recoveredTimer));
          setHasRecoveredTimer(true);
          toast.success('Timer recovered from previous session');
          return;
        }
      }

      if (serverTimer && localTimer && serverTimer.id !== localTimer.id) {
        // Resolve conflict - prefer server timer
        const resolvedTimer = await timerPersistence.resolveConflict(serverTimer, localTimer, userId);
        setActiveTimer(resolvedTimer);
        setElapsedTime(timerPersistence.getElapsedTime(resolvedTimer));
        return;
      }

      if (serverTimer) {
        setActiveTimer(serverTimer);
        setElapsedTime(timerPersistence.getElapsedTime(serverTimer));
        timerPersistence.saveTimer(serverTimer, userId);
      } else if (localTimer) {
        // Use local timer if no server timer
        setActiveTimer(localTimer);
        setElapsedTime(timerPersistence.getElapsedTime(localTimer));
      } else {
        setActiveTimer(null);
        setElapsedTime(0);
        timerPersistence.saveTimer(null, userId);
      }
    } catch (error) {
      console.error('Error loading active timer:', error);
      // Fallback to local timer if available
      if (userId) {
        const localTimer = timerPersistence.loadTimer(userId);
        if (localTimer) {
          setActiveTimer(localTimer);
          setElapsedTime(timerPersistence.getElapsedTime(localTimer));
        }
      }
    }
  }, [userId]);

  const startTimer = useCallback(async (taskId?: string, description?: string, clientId?: string) => {
    if (!userId) return;

    setLoading(true);
    try {
      const response = await api.post('/time-entries/start-timer', {
        taskId: taskId || undefined,
        clientId: clientId || undefined,
        description: description || 'Timer session'
      });

      if (response.data.success) {
        const timer = {
          ...response.data.data,
          startTime: new Date(response.data.data.startTime)
        };
        setActiveTimer(timer);
        setElapsedTime(0);
        timerPersistence.saveTimer(timer, userId);
        toast.success('Timer started');
      }
    } catch (error: any) {
      console.error('Error starting timer:', error);
      
      let message = 'Failed to start timer';
      if (error.response?.data?.error?.message) {
        const serverMessage = error.response.data.error.message;
        if (serverMessage.includes('active timer')) {
          message = 'You already have an active timer running. Please stop it first before starting a new one.';
        } else {
          message = serverMessage;
        }
      } else if (error.response?.data?.message) {
        message = error.response.data.message;
      }
      
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const stopTimer = useCallback(async () => {
    if (!activeTimer || !userId) return;

    setLoading(true);
    try {
      const response = await api.patch(`/time-entries/${activeTimer.id}/stop`);
      
      if (response.data.success) {
        const stoppedEntry = response.data.data;
        setActiveTimer(null);
        setElapsedTime(0);
        timerPersistence.saveTimer(null, userId);
        
        // Duration from backend is in minutes, convert to display format
        const totalMinutes = stoppedEntry.duration;
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        const timeStr = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

        toast.success(`Timer stopped - ${timeStr} recorded`);
      }
    } catch (error: any) {
      console.error('Error stopping timer:', error);
      
      let message = 'Failed to stop timer';
      if (error.response?.data?.error?.message) {
        message = error.response.data.error.message;
      } else if (error.response?.data?.message) {
        message = error.response.data.message;
      }
      
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [activeTimer, userId]);

  const createTimeEntry = useCallback(async (entry: Partial<TimeEntry>) => {
    setLoading(true);
    try {
      const response = await api.post('/time-entries', {
        taskId: entry.taskId || undefined,
        description: entry.description,
        startTime: entry.startTime || new Date(),
        endTime: entry.endTime,
        duration: entry.duration,
        billable: entry.billable !== undefined ? entry.billable : true
      });

      if (response.data.success) {
        toast.success('Time entry created');
        return response.data.data;
      }
    } catch (error: any) {
      console.error('Error creating time entry:', error);
      toast.error('Failed to create time entry');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const getTimeEntries = useCallback(async (filters?: any) => {
    try {
      const response = await api.get('/time-entries', { params: filters });
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching time entries:', error);
      throw error;
    }
  }, []);

  const getTimeStats = useCallback(async (startDate?: Date, endDate?: Date) => {
    try {
      const params: any = {};
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();
      
      const response = await api.get('/time-entries/stats', { params });
      return response.data.data || {};
    } catch (error) {
      console.error('Error fetching time stats:', error);
      throw error;
    }
  }, []);

  const value: TimerContextType = {
    activeTimer,
    elapsedTime,
    loading,
    hasRecoveredTimer,
    startTimer,
    stopTimer,
    loadActiveTimer,
    createTimeEntry,
    getTimeEntries,
    getTimeStats
  };

  return (
    <TimerContext.Provider value={value}>
      {children}
    </TimerContext.Provider>
  );
};

export const useTimer = (): TimerContextType => {
  const context = useContext(TimerContext);
  if (context === undefined) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
};
